import { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Header from "@/components/feature/Header";
import Footer from "@/components/feature/Footer";
import JsonLd from "@/components/base/JsonLd";
import { articleList, getArticleSections } from "@/mocks/article";
import { MAIN_SITE_URL } from "@/mocks/categories";

const SITE_URL = (import.meta.env.VITE_SITE_URL as string | undefined)?.replace(/\/+$/, "") || "";

export default function ArticlePage() {
  const { t, i18n } = useTranslation();
  const { slug } = useParams();

  const article = articleList.find((a) => a.slug === slug);
  const sections = slug ? getArticleSections(i18n.language, slug) : [];

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!article) {
    return (
      <div className="min-h-screen flex flex-col bg-background-50">
        <Header />
        <main className="flex-1 flex items-center justify-center">
          <div className="text-center px-4">
            <h1 className="text-2xl font-heading font-bold text-foreground-950">
              {t("article.notFound")}
            </h1>
            <Link
              to={`/${i18n.language}`}
              className="mt-4 inline-flex items-center gap-2 rounded-full bg-primary-500 px-6 py-3 text-sm font-bold text-background-50 hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              {t("article.backHome")}
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const title = t(`article.${article.id}.title`);
  const description = t(`article.${article.id}.excerpt`);
  const pageUrl = `${SITE_URL}/${i18n.language}/article/${article.slug}`;

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: title,
    description,
    image: article.image,
    url: pageUrl,
    mainEntityOfPage: pageUrl,
    inLanguage: i18n.language,
    datePublished: "2026-01-15",
    dateModified: "2026-08-20",
    author: {
      "@type": "Organization",
      name: "USFansSheet",
    },
    publisher: {
      "@type": "Organization",
      name: "USFansSheet",
    },
  };

  return (
    <div className="min-h-screen flex flex-col bg-background-50">
      <JsonLd data={articleSchema} />
      <Header />
      <main className="flex-1">
        {/* Hero */}
        <section className="w-full bg-background-50">
          <div className="mx-auto max-w-3xl px-4 md:px-6 pt-8 pb-4">
            <Link
              to={`/${i18n.language}`}
              className="inline-flex items-center gap-2 text-sm font-semibold text-primary-600 hover:text-primary-700 cursor-pointer"
            >
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-left-line text-sm"></i>
              </span>
              {t("article.backHome")}
            </Link>

            <div className="mt-6">
              <div className="flex flex-wrap items-center gap-3 text-xs font-semibold text-foreground-500">
                <span className="rounded-full bg-accent-100 text-accent-900 px-3 py-1">
                  {article.category}
                </span>
                <span className="flex items-center gap-1">
                  <span className="w-3 h-3 flex items-center justify-center">
                    <i className="ri-time-line text-xs"></i>
                  </span>
                  {article.readMinutes} {t("article.readTime")}
                </span>
                <span className="flex items-center gap-1">
                  <span className="w-3 h-3 flex items-center justify-center">
                    <i className="ri-refresh-line text-xs"></i>
                  </span>
                  {t("article.updated")}: 2026
                </span>
              </div>

              <h1 className="mt-4 text-3xl md:text-4xl font-heading font-bold leading-tight text-foreground-950">
                {title}
              </h1>
            </div>
          </div>
        </section>

        {/* Hero image */}
        <section className="w-full bg-background-50">
          <div className="mx-auto max-w-4xl px-4 md:px-6">
            <div className="h-56 md:h-80 overflow-hidden rounded-2xl">
              <img
                src={article.image}
                alt={title}
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>
        </section>

        {/* Body */}
        <article className="w-full bg-background-50">
          <div className="mx-auto max-w-3xl px-4 md:px-6 py-10">
            {sections.map((section) => (
              <section key={section.id} className="mb-10">
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-4">
                  {section.heading}
                </h2>
                {section.paragraphs.map((p, i) => (
                  <p key={i} className="mb-4 text-base text-foreground-700 leading-relaxed">
                    {p}
                  </p>
                ))}
                {section.bullets && (
                  <ul className="space-y-2 mt-2">
                    {section.bullets.map((b, i) => (
                      <li key={i} className="flex items-start gap-3 text-base text-foreground-700">
                        <span className="w-5 h-5 flex items-center justify-center text-accent-500 shrink-0 mt-0.5">
                          <i className="ri-check-line text-base"></i>
                        </span>
                        {b}
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            ))}

            {/* Internal link + CTA */}
            <div className="mt-12 rounded-2xl border border-background-200 bg-background-100 p-6">
              <p className="text-sm text-foreground-700 leading-relaxed">
                {t("article.related")}
              </p>
              <div className="mt-5 flex flex-col sm:flex-row gap-3">
                <Link
                  to={`/${i18n.language}`}
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-primary-500 px-6 py-3 text-sm font-bold text-background-50 hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                >
                  {t("article.backHome")}
                </Link>
                <a
                  href={MAIN_SITE_URL}
                  target="_blank"
                  rel="noopener nofollow"
                  className="inline-flex items-center justify-center gap-2 rounded-full border border-foreground-300 bg-background-50 px-6 py-3 text-sm font-bold text-foreground-900 hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap"
                >
                  {t("hero.ctaSecondary")}
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-arrow-right-up-line text-sm"></i>
                  </span>
                </a>
              </div>
            </div>
          </div>
        </article>
      </main>
      <Footer />
    </div>
  );
}